import { Flex, Box, Text, Tooltip, HStack } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faArrowLeft,
  faArrowDownToBracket,
  faCopy,
  faCalendarDay,
  faFileLines,
  faAngleRight,
  faBookSparkles
} from '@fortawesome/pro-regular-svg-icons';
import {
  ButtonIcon,
  ButtonText,
  Link,
  Modal,
  useToast
} from '@morressier/alembic';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { useMemo, useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDate } from 'src/utils/date';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { downloadFile } from 'src/utils/file';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import TextWithIcon from 'src/components/TextWithIcon';
import { formatAuthorFull, formatAuthorLocation } from 'src/utils/formatAuthor';
import ManageTags from './ManageTags';
import AnomalyDetectionIcon from 'src/components/AnomalyDetectionIcon';
const MAX_VISIBLE = 1;

interface ArticleHeaderProps {
  onClose: () => void;
  onNext?: () => void;
  isNextEnabled?: boolean;
  isNextLoading?: boolean;
}

const HeaderInfo = ({
  onClose,
  onNext,
  isNextEnabled = false,
  isNextLoading = false
}: ArticleHeaderProps) => {
  const toast = useToast();
  const { getFieldState } = useSubmissionFields('submission');
  const Authors = getFieldState<FormFieldAuthors>('Authors');
  const {
    workflowResponse: { workflowData, manuscriptId, isUADDetected },
    fieldsResponse,
    submissionContext
  } = useSubmissionData();
  const submissionType = fieldsResponse.articleTypeName;
  const title = fieldsResponse.fields.Title!.value as string;
  const submissionDate = workflowData.states?.find(state =>
    ['initial', 'transferOffered'].includes(state.name)
  )?.completedWith?.date;

  const selectedFocusIssue = submissionContext?.data?.focusIssues?.find(
    item => item.id === fieldsResponse?.fields?.FocusIssue?.value
  );
  const enableConsolidatedPdf = submissionContext?.config.enableConsolidatedPdf;
  const handleCopy = async (variable: string) => {
    await navigator.clipboard.writeText(variable);
    toast({
      variant: 'success',
      title: 'Copied to clipboard',
      description: '',
      isClosable: true
    });
  };
  const [showAll, setShowAll] = useState(false);
  const authors = useMemo(
    () => (Array.isArray(Authors?.value) ? Authors.value : []),
    [Authors]
  );
  const manuscriptUrl = enableConsolidatedPdf
    ? (fieldsResponse?.fields?.ConsolidatedPdf?.value as ISubmissionFile)?.url
    : (fieldsResponse?.fields?.Manuscript?.value as ISubmissionFile)?.url;

  const titlePageUrl = (
    fieldsResponse?.fields?.TitlePage?.value as ISubmissionFile
  )?.url;

  const remainingCount = authors.length - MAX_VISIBLE;
  const shouldShowToggle = authors.length > MAX_VISIBLE;

  return (
    <>
      <Box
        p={4}
        border="1px solid"
        borderColor="gray.200"
        bgColor="surfacePrimaryFill"
      >
        <Flex flexDirection={{ base: 'column', xl: 'row' }} gap={2}>
          <Flex flexDirection="column" width={{ base: '100%' }}>
            <HStack spacing={2} mb={2}>
              {onClose && (
                <ButtonText
                  size="regular"
                  onClick={onClose}
                  leftIcon={<FontAwesomeIcon icon={faArrowLeft} size="lg" />}
                >
                  Back
                </ButtonText>
              )}
              <Box borderRight="1px solid" borderColor="neutralPrimaryBorder" />
              <Text as="span" textStyle={'headingItem'} fontWeight="semibold">
                # {manuscriptId}
              </Text>
              <Tooltip
                label="No more submissions"
                hasArrow
                placement="top"
                // Only explain a disabled button: hidden while it is usable and
                // while a next submission is being fetched.
                isDisabled={isNextEnabled || isNextLoading}
                shouldWrapChildren
              >
                <ButtonText
                  rightIcon={<FontAwesomeIcon icon={faAngleRight} />}
                  fontSize="sm"
                  onClick={onNext}
                  disabled={!isNextEnabled || isNextLoading}
                  loading={{ isLoading: isNextLoading, loadingText: 'Next' }}
                >
                  Next
                </ButtonText>
              </Tooltip>
            </HStack>
            <Flex alignItems={'center'} gap={1}>
              <Box textStyle="headingItem" mb={2} noOfLines={1}>
                <RichTextPreview value={title} />
              </Box>{' '}
              {isUADDetected && (
                <Tooltip
                  label={'Unusual activities are detected for this submission'}
                  hasArrow
                  placement="top"
                >
                  <Box mt={-1.5}>
                    <AnomalyDetectionIcon />
                  </Box>
                </Tooltip>
              )}
            </Flex>

            <Flex gap={1} flexDirection="column">
              <Flex flexDirection="row" gap={2} flexWrap="wrap">
                <TextWithIcon
                  icon={faCalendarDay}
                  text={formatDate(new Date(submissionDate ?? ''))}
                />
                <TextWithIcon icon={faFileLines} text={submissionType} />
                {selectedFocusIssue && (
                  <Flex flexDirection="row" alignContent="center" gap={2}>
                    <TextWithIcon
                      icon={faBookSparkles}
                      text={'Special Issue: '}
                    />
                    <Tooltip
                      label={selectedFocusIssue.name}
                      hasArrow
                      placement="top"
                    >
                      <Box maxW="220px" minW="0">
                        {selectedFocusIssue?.issueLink ||
                        submissionContext?.data?.guidelineLink ? (
                          <Link
                            variant="primary"
                            href={
                              selectedFocusIssue?.issueLink ||
                              submissionContext?.data?.guidelineLink
                            }
                            target="_blank"
                            style={{ display: 'block' }}
                          >
                            <Text textStyle="textContext" isTruncated>
                              {selectedFocusIssue.name}
                            </Text>
                          </Link>
                        ) : (
                          <Text textStyle="textContext" isTruncated>
                            {selectedFocusIssue.name}
                          </Text>
                        )}
                      </Box>
                    </Tooltip>
                  </Flex>
                )}
                {manuscriptUrl && (
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
                    fontSize="sm"
                    onClick={() => {
                      downloadFile(manuscriptUrl);
                    }}
                  >
                    Manuscript
                  </ButtonText>
                )}
                {titlePageUrl && !enableConsolidatedPdf && (
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
                    fontSize="sm"
                    onClick={() => {
                      downloadFile(titlePageUrl);
                    }}
                  >
                    Title page
                  </ButtonText>
                )}
              </Flex>
              <Flex
                wrap="wrap"
                columnGap={1}
                alignItems="center"
                width="100%"
                minWidth={0}
              >
                {authors.length >= 1 && (
                  <Flex align="center" minWidth={0} overflow="hidden">
                    <Box flexShrink={0}>
                      <TextWithIcon icon={faUser} />
                    </Box>
                    <Tooltip
                      label={formatAuthorFull(authors[0]!)}
                      isDisabled={!formatAuthorLocation(authors[0]!)}
                      hasArrow
                      placement="top"
                    >
                      <Text isTruncated textStyle="textContext" minWidth={0}>
                        <Text as="span" fontWeight="500">
                          {`${authors[0]?.firstName} ${authors[0]?.lastName}`}
                        </Text>
                        {formatAuthorLocation(authors[0]!)
                          ? `, ${formatAuthorLocation(authors[0]!)}`
                          : ''}
                      </Text>
                    </Tooltip>
                    <Box flexShrink={0}>
                      <ButtonIcon
                        variant="primary"
                        ariaLabel="copytoclipboard"
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        size="small"
                        onClick={() =>
                          handleCopy(formatAuthorFull(authors[0]!))
                        }
                      />
                    </Box>
                  </Flex>
                )}

                {shouldShowToggle && (
                  <ButtonText
                    variant="outline"
                    fontSize={'sm'}
                    onClick={() => setShowAll(prev => !prev)}
                  >
                    {showAll
                      ? 'Show Less'
                      : `+${remainingCount} author${
                          remainingCount > 1 ? 's' : ''
                        }`}
                  </ButtonText>
                )}
              </Flex>
            </Flex>
            <ManageTags />
          </Flex>
        </Flex>
      </Box>
      <Modal isOpen={showAll} width="720px" onClose={() => setShowAll(false)}>
        <Flex padding={4} flexDirection="column" gap={4}>
          <Text textStyle="headingGroup">Authors</Text>
          <Flex flexDirection="column">
            {authors.map((author, idx) => (
              <Flex key={idx} align="center" gap={1}>
                <Tooltip
                  label={formatAuthorFull(author)}
                  isDisabled={!formatAuthorLocation(author)}
                  hasArrow
                  placement="top"
                >
                  <Text isTruncated textStyle="textContext">
                    <Text as="span" fontWeight="500">
                      {`${author.firstName} ${author.lastName}`}
                    </Text>
                    {formatAuthorLocation(author)
                      ? `, ${formatAuthorLocation(author)}`
                      : ''}
                  </Text>
                </Tooltip>
                <ButtonIcon
                  variant="primary"
                  ariaLabel="copytoclipboard"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  size="small"
                  onClick={() => handleCopy(formatAuthorFull(author))}
                />
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default HeaderInfo;
